<template>
  <v-sheet class="mx-auto" max-width="2000">
    <v-card-text>
      <v-card class="mx-auto" elevation="0" title="Demographic baseline numbers"
              ref="demographic-baseline-numbers">
        <v-card-subtitle>Use the information icon, in the top right corner, to see the population definition, and
          use the filter icon to filter the cohort.</v-card-subtitle>
        <v-divider :vertical="true"></v-divider>
        <v-card-subtitle class="no-dim text-primary">Total</v-card-subtitle>
        <v-row no-gutters>
          <v-col cols="12" sm="10" md="8" lg="6" xl="4" xxl="4">
            <number-box :number="demographicBaselineTotalNumberOfPatients.value"
                        :label="demographicBaselineTotalNumberOfPatients.label"
                        :comment="demographicBaselineTotalNumberOfPatients.comment"/>
          </v-col>
          <v-col cols="12" sm="10" md="8" lg="6" xl="4" xxl="4">
            <combined-pie-chart-and-table :chart-data="demographicBaselineAgeChartData"
                                          :chart-comment="demographicBaselineAgeChartComment"
                                          :table-headers="demographicBaselineNumbersHeaders"
                                          :table-data="demographicBaselineAgeNumbers"
                                          :table-comment="demographicBaselineAgeComment"
                                          :visible="visible"/>
          </v-col>
          <v-col cols="12" sm="10" md="8" lg="6" xl="4" xxl="4">
            <combined-pie-chart-and-table :chart-data="demographicBaselineSexChartData"
                                          :chart-comment="demographicBaselineSexChartComment"
                                          :table-headers="demographicBaselineNumbersHeaders"
                                          :table-data="demographicBaselineSexNumbers"
                                          :table-comment="demographicBaselineSexComment"
                                          :visible="visible"/>
          </v-col>
        </v-row>
        <v-card-subtitle class="no-dim text-primary">Filtered</v-card-subtitle>
        <v-row no-gutters>
          <v-col cols="12" sm="10" md="8" lg="6" xl="4" xxl="4">
            <number-box :number="filteredDemographicBaselineTotalNumberOfPatients.value"
                        :label="filteredDemographicBaselineTotalNumberOfPatients.label"
                        :comment="filteredDemographicBaselineTotalNumberOfPatients.comment"/>
          </v-col>
          <v-col cols="12" sm="10" md="8" lg="6" xl="4" xxl="4">
            <combined-pie-chart-and-table :chart-data="filteredDemographicBaselineAgeChartData"
                                          :chart-comment="filteredDemographicBaselineAgeChartComment"
                                          :table-headers="demographicBaselineNumbersHeaders"
                                          :table-data="filteredDemographicBaselineAgeNumbers"
                                          :table-comment="filteredDemographicBaselineAgeComment"
                                          :visible="visible"
                                          v-on:itemSelected="filteredDemographicBaselineChartItemSelected($event)"/>
          </v-col>
          <v-col cols="12" sm="10" md="8" lg="6" xl="4" xxl="4">
            <combined-pie-chart-and-table :chart-data="filteredDemographicBaselineSexChartData"
                                          :chart-comment="filteredDemographicBaselineSexChartComment"
                                          :table-headers="demographicBaselineNumbersHeaders"
                                          :table-data="filteredDemographicBaselineSexNumbers"
                                          :table-comment="filteredDemographicBaselineSexComment"
                                          :visible="visible"
                                          v-on:itemSelected="filteredDemographicBaselineChartItemSelected($event)"/>
          </v-col>
        </v-row>
        <v-row no-gutters>
          <v-col class="text-right">
            <vue-to-print :content="() => this.$refs['demographic-baseline-numbers'].$el"
                          :document-title="icd + '-' + 'demographic-baseline-numbers'"
                          remove-after-print>
              <template v-slot:trigger>
                <v-btn variant="plain" icon="mdi-printer" class="end"></v-btn>
              </template>
            </vue-to-print>
          </v-col>
        </v-row>
      </v-card>
      <v-row no-gutters>
        <v-col cols="12" sm="10" md="8" lg="6" xl="6" xxl="6">
          <v-card class="mx-auto" min-height="800" elevation="0" title="Filtered number of new cases"
                  ref="number-of-diagnosed-patients">
            <v-card-subtitle class="text-wrap">{{ totalNumberOfPatientsPerYearChartSubtitle }}</v-card-subtitle>
            <v-row no-gutters>
              <v-col>
                <GChart type="BarChart"
                        :data="totalNumberOfPatientsPerYearChartData"
                        :options="totalNumberOfPatientsPerYearChartOptions"
                        :key="'totalNumberOfPatientsPerYearChart' + totalNumberOfPatientsPerYearChartKeyIndex"
                        v-if="totalNumberOfPatientsPerYearChartData.length !== 0"/>
                <v-progress-circular color="primary" indeterminate v-else></v-progress-circular>
              </v-col>
            </v-row>
            <v-row no-gutters>
              <v-col class="text-right">
                <vue-to-print :content="() => this.$refs['number-of-diagnosed-patients'].$el"
                              :document-title="icd + '-' + 'number-of-diagnosed-patients'"
                              remove-after-print>
                  <template v-slot:trigger>
                    <v-btn variant="plain" icon="mdi-printer" class="end"></v-btn>
                  </template>
                </vue-to-print>
              </v-col>
            </v-row>
          </v-card>
        </v-col>
        <v-col cols="12" sm="10" md="8" lg="6" xl="6" xxl="6">
          <v-card class="mx-auto" min-height="800" elevation="0" title="Filtered number of deaths"
                  ref="number-of-deceased-patients">
            <v-card-subtitle class="text-wrap">{{ totalNumberOfDeceasedPatientsPerYearChartSubtitle }}</v-card-subtitle>
            <v-row no-gutters>
              <v-col>
                <GChart type="BarChart" :data="totalNumberOfDeceasedPatientsPerYearChartData"
                        :options="totalNumberOfDeceasedPatientsPerYearChartOptions"
                        :key="'totalNumberOfDeceasedPatientsPerYearChart' + totalNumberOfDeceasedPatientsPerYearChartKeyIndex"
                        v-if="totalNumberOfDeceasedPatientsPerYearChartData.length !== 0"/>
                <v-progress-circular color="primary" indeterminate v-else></v-progress-circular>
              </v-col>
            </v-row>
            <v-row no-gutters>
              <v-col class="text-right">
                <vue-to-print :content="() => this.$refs['number-of-deceased-patients'].$el"
                              :document-title="icd + '-' + 'number-of-deceased-patients'"
                              remove-after-print>
                  <template v-slot:trigger>
                    <v-btn variant="plain" icon="mdi-printer" class="end"></v-btn>
                  </template>
                </vue-to-print>
              </v-col>
            </v-row>
          </v-card>
        </v-col>
      </v-row>
      <v-row no-gutters>
        <v-col cols="12" lg="5" xl="5" xxl="5">
          <v-card class="mx-auto" elevation="0"
                  title="Age at index date"
                  ref="age-at-index-date">
            <v-card-subtitle class="text-wrap">{{this.demographicBaselineAgeAtIndexDateComment}}</v-card-subtitle>
            <v-row no-gutters>
              <v-col>
                <v-data-table :headers="demographicBaselineAgeAtIndexDateHeaders"
                              :items="demographicBaselineAgeAtIndexDateData"
                              density="compact"
                              :fixed-header="true"
                              :items-per-page="0"
                              class="striped-table">
                  <template #bottom></template>
                  <template v-slot:no-data>
                    <v-progress-circular color="primary" indeterminate></v-progress-circular>
                  </template>
                </v-data-table>
              </v-col>
            </v-row>
            <v-row no-gutters>
              <v-col class="text-right">
                <vue-to-print :content="() => this.$refs['age-at-index-date'].$el"
                              :document-title="icd + '-' + 'age-at-index-date'"
                              remove-after-print>
                  <template v-slot:trigger>
                    <v-btn variant="plain" icon="mdi-printer" class="end"></v-btn>
                  </template>
                </vue-to-print>
              </v-col>
            </v-row>
          </v-card>
        </v-col>
        <v-col cols="0" lg="1" xl="1" xxl="1"/>
        <v-col cols="12" lg="5" xl="5" xxl="5">
          <v-card class="mx-auto" elevation="0"
                  title="Comorbidity"
                  ref="charlson-comorbidity">
            <!-- eslint-disable-next-line vue/no-v-text-v-html-on-component -->
            <v-card-subtitle class="text-wrap" v-html="this.demographicBaselineCharlsonComorbidityComment"></v-card-subtitle>
            <v-row no-gutters>
              <v-col>
                <v-data-table :headers="demographicBaselineCharlsonComorbidityHeaders"
                              :items="demographicBaselineCharlsonComorbidityData"
                              density="compact"
                              :fixed-header="true"
                              :items-per-page="0"
                              class="striped-table">
                  <template #bottom></template>
                  <template v-slot:no-data>
                    <v-progress-circular color="primary" indeterminate></v-progress-circular>
                  </template>
                </v-data-table>
              </v-col>
            </v-row>
            <v-row no-gutters>
              <v-col class="text-right">
                <vue-to-print :content="() => this.$refs['charlson-comorbidity'].$el"
                              :document-title="icd + '-' + 'charlson-comorbidity'"
                              remove-after-print>
                  <template v-slot:trigger>
                    <v-btn variant="plain" icon="mdi-printer" class="end"></v-btn>
                  </template>
                </vue-to-print>
              </v-col>
            </v-row>
          </v-card>
        </v-col>
      </v-row>
    </v-card-text>
  </v-sheet>
</template>

<script>
import {defineComponent} from 'vue';
import {GChart} from 'vue-google-charts'
import axios from "axios";
import { VueToPrint } from "vue-to-print";
import CombinedPieChartAndTable from "@/components/CombinedPieChartAndTable.vue";
import NumberBox from "@/components/NumberBox.vue";

function sortCharlsonComorbidityIndex(a, b) {
  const order = {
    '0': 0,
    '1': 1,
    '2': 2,
    '≥3': 3,
  }

  return order[a] - order[b]
}

export default defineComponent({
  name: 'InternalOrderCancerDemographicBaseline',
  props: {
    icd: String,
    diseaseDescription: String,
    ageAtDiagnosis: Array,
    sex: Array,
    diagnosisDate: Array,
    visible: Boolean
  },
  components: {
    NumberBox,
    GChart,
    CombinedPieChartAndTable,
    VueToPrint
  },
  data: context => ({
    loading: false,
    demographicBaselineTotalNumberOfPatients: {},
    demographicBaselineNumbersHeaders: [
      {
        title: 'Patient population',
        align: 'start',
        key: 'label',
      },
      {
        title: 'Value',
        align: 'end',
        key: 'value',
      },
      {
        title: 'Percentage',
        align: 'end',
        key: 'percentage',
      }],
    demographicBaselineAgeNumbers: [],
    demographicBaselineAgeComment: '',
    demographicBaselineSexNumbers: [],
    demographicBaselineSexComment: '',
    demographicBaselineAgeChartData: [],
    demographicBaselineAgeChartComment: '',
    demographicBaselineSexChartData: [],
    demographicBaselineSexChartComment: '',
    demographicBaselineAgeAtIndexDateHeaders: [
      {
        title: 'Patient population',
        align: 'start',
        key: 'label',
      },
      {
        title: 'Number of patients',
        align: 'start',
        key: 'number_of_patients',
      },
      {
        title: 'Mean (SD)',
        align: 'start',
        key: 'mean_age',
      },
      {
        title: 'Median (Q1, Q3)',
        align: 'start',
        key: 'median_age',
      },
      {
        title: 'Range (Min, Max)',
        align: 'start',
        key: 'range_age',
      }
    ],
    demographicBaselineAgeAtIndexDateData: [],
    demographicBaselineAgeAtIndexDateComment: '',
    demographicBaselineCharlsonComorbidityHeaders: [
      {
        title: 'Patient population',
        align: 'start',
        key: 'label',
        sortable: true
      },
      {
        title: 'Charlson Comorbidity Index (CCI)',
        align: 'start',
        key: 'charlson_comorbidity_index',
        sortable: true,
        sort: (a, b) => sortCharlsonComorbidityIndex(a, b)
      },
      {
        title: 'Number of patients',
        align: 'start',
        key: 'number_of_patients',
        sortable: true
      }
    ],
    demographicBaselineCharlsonComorbidityData: [],
    demographicBaselineCharlsonComorbidityComment: '',
    filteredDemographicBaselineTotalNumberOfPatients: {},
    filteredDemographicBaselineAgeNumbers: [],
    filteredDemographicBaselineAgeComment: '',
    filteredDemographicBaselineSexNumbers: [],
    filteredDemographicBaselineSexComment: '',
    selectedFilteredDemographicBaseline: 'filteredDemographicBaselinePieChartValue',
    filteredDemographicBaselineAgeChartData: [],
    filteredDemographicBaselineAgeChartComment: '',
    filteredDemographicBaselineSexChartData: [],
    filteredDemographicBaselineSexChartComment: '',
    totalNumberOfPatientsPerYearChartKeyIndex: 0,
    totalNumberOfPatientsPerYearChartSubtitle: 'The diagnosis date is defined as the date of ' +
        context.diseaseDescription + ' first recorded for the patient in the Cancer Register.',
    totalNumberOfPatientsPerYearChartData: [],
    totalNumberOfPatientsPerYearChartOptions: {
      title: 'New cases per year',
      backgroundColor: {
        fill: '#364c5b'
      },
      height: 800,
      colors: ['#b2df8a'],
      titleTextStyle: {
        fontSize: 16,
        color: '#ffffff',
        bold: true
      },
      legend: 'none',
      hAxis: {
        title: '',
        baselineColor: '#f0f0f0',
        textStyle: {
          color: '#ffffff',
          fontSize: 12
        },
        gridlines: {
          color: '#c0c0c0'
        },
        minorGridlines: {
          count: 0
        }
      },
      vAxis: {
        title: '',
        baselineColor: '#f0f0f0',
        gridlines: {
          count: 0
        },
        minorGridlines: {
          count: 0
        },
        textStyle: {
          color: '#ffffff',
          fontSize: 12
        }
      },
      annotations: {
        textStyle: {
          fontSize: 12,
          color: '#808080',
          auraColor: 'none'
        }
      },
      tooltip: {
        isHtml: true
      },
      animation: {
        startup: true,
        duration: 500,
        easing: 'out'
      }
    },
    totalNumberOfDeceasedPatientsPerYearChartKeyIndex: 0,
    totalNumberOfDeceasedPatientsPerYearChartSubtitle: '',
    totalNumberOfDeceasedPatientsPerYearChartData: [],
    totalNumberOfDeceasedPatientsPerYearChartOptions: {
      title: 'Deaths per year',
      backgroundColor: {
        fill: '#364c5b'
      },
      height: 800,
      colors: ['#fdbf6f'],
      titleTextStyle: {
        fontSize: 16,
        color: '#ffffff',
        bold: true
      },
      legend: 'none',
      hAxis: {
        title: '',
        baselineColor: '#f0f0f0',
        textStyle: {
          color: '#ffffff',
          fontSize: 12
        },
        gridlines: {
          color: '#c0c0c0'
        },
        minorGridlines: {
          count: 0
        }
      },
      vAxis: {
        title: '',
        baselineColor: '#f0f0f0',
        gridlines: {
          count: 0
        },
        minorGridlines: {
          count: 0
        },
        textStyle: {
          color: '#ffffff',
          fontSize: 12
        }
      },
      annotations: {
        textStyle: {
          fontSize: 12,
          color: '#808080',
          auraColor: 'none'
        }
      },
      tooltip: {
        isHtml: true
      },
      intervals: {
        'style': 'boxes',
        'boxWidth': 1,
        'lineWidth': 0,
        'fillOpacity': 0.2
      },
      animation: {
        startup: true,
        duration: 500,
        easing: 'out'
      }
    }
  }),
  methods: {
    forceRerender() {
      this.totalNumberOfPatientsPerYearChartKeyIndex += 1;
      this.totalNumberOfDeceasedPatientsPerYearChartKeyIndex += 1;
    },
    async loadDemographicBaselineTotalNumberOfPatientsData() {
      let data = axios.get('cancer-internal-order/demographic-baseline-total-number-of-patients',
          {
            headers: { Authorization: `Bearer ${this.$store.getters.stateUser.keycloak_token}` },
            params: {
              icd: this.icd
            }
          });
      return data.then((response) => {
        this.demographicBaselineTotalNumberOfPatients = response.data;
      });
    },
    async loadDemographicBaselineData() {
      let ageData = axios.get('cancer-internal-order/demographic-baseline-age',
          {
            headers: { Authorization: `Bearer ${this.$store.getters.stateUser.keycloak_token}` },
            params: {
              icd: this.icd
            }
          });
      let sexData = axios.get('cancer-internal-order/demographic-baseline-sex',
          {
            headers: { Authorization: `Bearer ${this.$store.getters.stateUser.keycloak_token}` },
            params: {
              icd: this.icd
            }
          });
      return await Promise.all([ageData, sexData]).then((responses) => {
        this.demographicBaselineAgeNumbers = responses[0].data.data;
        this.demographicBaselineAgeComment = responses[0].data.comment;
        this.demographicBaselineSexNumbers = responses[1].data.data;
        this.demographicBaselineSexComment = responses[1].data.comment;
      });
    },
    async loadDemographicBaselineChartData() {
      let ageChartData = axios.get('cancer-internal-order/demographic-baseline-age-chart',
          {
            headers: { Authorization: `Bearer ${this.$store.getters.stateUser.keycloak_token}` },
            params: {
              icd: this.icd
            }
          });
      let sexChartData = axios.get('cancer-internal-order/demographic-baseline-sex-chart',
          {
            headers: { Authorization: `Bearer ${this.$store.getters.stateUser.keycloak_token}` },
            params: {
              icd: this.icd
            }
          });
      return await Promise.all([ageChartData, sexChartData]).then((responses) => {
        this.demographicBaselineAgeChartData = responses[0].data.data
        this.demographicBaselineAgeChartComment = responses[0].data.comment
        this.demographicBaselineSexChartData = responses[1].data.data;
        this.demographicBaselineSexChartComment = responses[1].data.comment;
      });
    },
    async loadFilteredDemographicBaselineAgeAtIndexDateData() {
      let data = axios.get('cancer-internal-order/filtered-demographic-baseline-age-at-index-date-data',
          {
            headers: { Authorization: `Bearer ${this.$store.getters.stateUser.keycloak_token}` },
            params: {
              icd: this.icd,
              age_at_diagnosis: this.ageAtDiagnosis.join(","),
              sex: this.sex.join(",")
            }
          });
      return data.then((response) => {
        this.demographicBaselineAgeAtIndexDateData = response.data.data
        this.demographicBaselineAgeAtIndexDateComment = response.data.comment
      });
    },
    async loadFilteredDemographicBaselineCharlsonComorbidityData() {
      let data = axios.get('cancer-internal-order/filtered-demographic-baseline-charlson-comorbidity-data',
          {
            headers: { Authorization: `Bearer ${this.$store.getters.stateUser.keycloak_token}` },
            params: {
              icd: this.icd,
              age_at_diagnosis: this.ageAtDiagnosis.join(","),
              sex: this.sex.join(",")
            }
          });
      return data.then((response) => {
        this.demographicBaselineCharlsonComorbidityData = response.data.data
        this.demographicBaselineCharlsonComorbidityComment = response.data.comment
      });
    },
    async loadFilteredDemographicBaselineTotalNumberOfPatientsData() {
      let data = axios.get('cancer-internal-order/filtered-demographic-baseline-total-number-of-patients',
          {
            headers: { Authorization: `Bearer ${this.$store.getters.stateUser.keycloak_token}` },
            params: {
              icd: this.icd,
              age_at_diagnosis: this.ageAtDiagnosis.join(","),
              sex: this.sex.join(","),
              year_of_diagnosis: this.diagnosisDate.join(",")
            }
          });
      return data.then((response) => {
        this.filteredDemographicBaselineTotalNumberOfPatients = response.data
      })
    },
    async loadFilteredDemographicBaselineData() {
      let ageData = axios.get('cancer-internal-order/filtered-demographic-baseline-age',
          {
            headers: { Authorization: `Bearer ${this.$store.getters.stateUser.keycloak_token}` },
            params: {
              icd: this.icd,
              age_at_diagnosis: this.ageAtDiagnosis.join(","),
              sex: this.sex.join(","),
              year_of_diagnosis: this.diagnosisDate.join(",")
            }
          });
      let sexData = axios.get('cancer-internal-order/filtered-demographic-baseline-sex',
          {
            headers: { Authorization: `Bearer ${this.$store.getters.stateUser.keycloak_token}` },
            params: {
              icd: this.icd,
              age_at_diagnosis: this.ageAtDiagnosis.join(","),
              sex: this.sex.join(","),
              year_of_diagnosis: this.diagnosisDate.join(",")
            }
          });
      return await Promise.all([ageData, sexData]).then((responses) => {
        this.filteredDemographicBaselineAgeNumbers = responses[0].data.data
        this.filteredDemographicBaselineAgeComment = responses[0].data.comment
        this.filteredDemographicBaselineSexNumbers = responses[1].data.data
        this.filteredDemographicBaselineSexComment = responses[1].data.comment
      })
    },
    async loadFilteredDemographicBaselineChartData() {
      let ageData = axios.get('cancer-internal-order/filtered-demographic-baseline-age-chart',
          {
            headers: { Authorization: `Bearer ${this.$store.getters.stateUser.keycloak_token}` },
            params: {
              icd: this.icd,
              age_at_diagnosis: this.ageAtDiagnosis.join(","),
              sex: this.sex.join(","),
              year_of_diagnosis: this.diagnosisDate.join(",")
            }
          });
      let sexData = axios.get('cancer-internal-order/filtered-demographic-baseline-sex-chart',
          {
            headers: { Authorization: `Bearer ${this.$store.getters.stateUser.keycloak_token}` },
            params: {
              icd: this.icd,
              age_at_diagnosis: this.ageAtDiagnosis.join(","),
              sex: this.sex.join(","),
              year_of_diagnosis: this.diagnosisDate.join(",")
            }
          });
      return await Promise.all([ageData, sexData]).then((responses) => {
        this.filteredDemographicBaselineAgeChartData = responses[0].data.data
        this.filteredDemographicBaselineAgeChartComment = responses[0].data.comment
        this.filteredDemographicBaselineSexChartData = responses[1].data.data
        this.filteredDemographicBaselineSexChartComment = responses[1].data.comment
      });
    },
    filteredDemographicBaselineChartItemSelected(item) {
      if (item?.age_group !== undefined) {
        this.$emit('ageGroupSelected', item.age_group)
      }
      if (item?.sex !== undefined) {
        this.$emit('genderSelected', item.sex)
      }
    },
    async loadTotalNumberOfPatientsPerYearChartData() {
      let data = axios.get('cancer-internal-order/total-number-of-patients-per-year-data',
          {
            headers: { Authorization: `Bearer ${this.$store.getters.stateUser.keycloak_token}` },
            params: {
              icd: this.icd,
              age_at_diagnosis: this.ageAtDiagnosis.join(","),
              sex: this.sex.join(","),
              year_of_diagnosis: this.diagnosisDate.join(",")
            }
          });
      return data.then((response) => {
        this.totalNumberOfPatientsPerYearChartData = response.data
      })
    },
    async loadTotalNumberOfDeceasedPatientsPerYearChartData() {
      let data = axios.get('cancer-internal-order/total-number-of-deceased-patients-per-year-data',
          {
            headers: { Authorization: `Bearer ${this.$store.getters.stateUser.keycloak_token}` },
            params: {
              icd: this.icd,
              age_at_diagnosis: this.ageAtDiagnosis.join(","),
              sex: this.sex.join(","),
              year_of_diagnosis: this.diagnosisDate.join(",")
            }
          });
      return data.then((response) => {
        this.totalNumberOfDeceasedPatientsPerYearChartData = response.data
      })
    },
    async loadDynamicData() {
      return await Promise.all([
        this.loadFilteredDemographicBaselineTotalNumberOfPatientsData(),
        this.loadFilteredDemographicBaselineData(),
        this.loadFilteredDemographicBaselineChartData(),
        this.loadTotalNumberOfPatientsPerYearChartData(),
        this.loadTotalNumberOfDeceasedPatientsPerYearChartData(),
        this.loadFilteredDemographicBaselineAgeAtIndexDateData(),
        this.loadFilteredDemographicBaselineCharlsonComorbidityData()
      ]).then(() => {
        console.log('Dynamic data loaded: NeurologicalDiseaseDemographicBaseline')
      })
    }
  },
  async mounted() {
    this.loading = true;
    await Promise.all([
      this.loadDemographicBaselineTotalNumberOfPatientsData(),
      this.loadDemographicBaselineData(),
      this.loadDemographicBaselineChartData()
    ]).then(() => {
      this.loadDynamicData()
    }).finally(() => {
      this.loading = false;
    });
  },
  updated() {
    if (this.visible) {
      this.loading = true;
      this.loadDynamicData()
          .then(() => this.forceRerender())
          .finally(() => {
            this.loading = false;
          });
    }
  }
});
</script>

<style scoped>
.striped-table :deep(tbody tr:nth-child(odd)) {
  background-color: rgba(0, 0, 0, .10);
}

.no-dim { opacity: 1 !important; } /* cancels Vuetify's dimming */

:deep(.v-card-subtitle a) {
  color: inherit;
  text-decoration: none;
  font-style: italic;
  font-weight: normal;
}

@media print {
  .mx-auto {
    zoom: 50%;
  }
}
</style>
